import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, InputAdornment,
  Tooltip, Skeleton, Stack, Snackbar, Alert, Checkbox, Switch, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Lock as LockIcon, Clear as ClearIcon,
  CheckCircle, Cancel, ContentCopy as CopyIcon,
  Visibility, VisibilityOff
} from '@mui/icons-material';
import { userAPI } from '../../services/api';
import UserAvatar from '../common/UserAvatar';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';
import TablePagination from '../common/TablePagination';

const UserManagement = () => {
  const { can } = useAccessControl();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [showCreatedUserDialog, setShowCreatedUserDialog] = useState(false);
  const [createdUserCredentials, setCreatedUserCredentials] = useState({ loginId: '', loginType: '', password: '' });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const canReadUsers = can(RESOURCES.USERS, ACTIONS.READ);
  const canCreateUsers = can(RESOURCES.USERS, ACTIONS.CREATE);
  const canUpdateUsers = can(RESOURCES.USERS, ACTIONS.UPDATE);
  const canDeleteUsers = can(RESOURCES.USERS, ACTIONS.DELETE);
  const canUseBulkActions = canUpdateUsers || canDeleteUsers;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
    role_id: '',
    staff_category: '',
    is_active: true
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    password_confirmation: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [roleFilter, statusFilter]);

  useEffect(() => {
    if (!canReadUsers) {
      setUsers([]);
      setRoles([]);
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [searchTerm, roleFilter, statusFilter, page, pageSize, canReadUsers]);

  useEffect(() => {
    if (canReadUsers) fetchRoles();
  }, [canReadUsers]);

  const fetchUsers = async () => {
    if (!canReadUsers) return;
    try {
      setLoading(true);
      const params = { page, per_page: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role_id = roleFilter;
      if (statusFilter !== '') params.is_active = statusFilter;

      const response = await userAPI.getAll(params);
      const data = response.data;
      setUsers(Array.isArray(data) ? data : data.data || []);
      setPagination({
        current_page: data.current_page ?? 1,
        last_page: data.last_page ?? 1,
        per_page: data.per_page ?? pageSize,
        total: data.total ?? 0,
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    if (!canReadUsers) return;
    try {
      const response = await userAPI.getRoles();
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingUser && !canUpdateUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to update users', severity: 'error' });
      return;
    }

    if (!editingUser && !canCreateUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to create users', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone_number: formData.phone_number,
          role_id: formData.role_id,
          staff_category: formData.staff_category || null,
          is_active: formData.is_active
        };
        await userAPI.update(editingUser.id, updateData);
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
        resetForm();
      } else {
        // Store password before creating user
        const tempPassword = formData.password;
        await userAPI.create(formData);
        
        // Show credentials dialog with login ID (email or phone)
        const loginId = formData.email || formData.phone_number;
        const loginType = formData.email ? 'Email' : 'Phone Number';
        setCreatedUserCredentials({
          loginId,
          loginType,
          password: tempPassword
        });
        setShowCreatedUserDialog(true);
        resetForm();
      }
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to save user';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    if (!canDeleteUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to delete users', severity: 'error' });
      return;
    }

    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleDelete = async () => {
    if (!canDeleteUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to delete users', severity: 'error' });
      return;
    }

    setDeleting(true);
    try {
      await userAPI.delete(userToDelete.id);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      handleCloseDeleteDialog();
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to delete user';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (id) => {
    if (!canUpdateUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to update users', severity: 'error' });
      return;
    }

    try {
      await userAPI.toggleStatus(id);
      setSnackbar({ open: true, message: 'User status updated', severity: 'success' });
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to update status';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleBulkAction = async (action) => {
    const requiresDelete = action === 'delete';
    const hasRequiredPermission = requiresDelete ? canDeleteUsers : canUpdateUsers;

    if (!hasRequiredPermission) {
      setSnackbar({ open: true, message: 'You do not have permission for this bulk action', severity: 'error' });
      return;
    }

    if (selectedUsers.length === 0) {
      setSnackbar({ open: true, message: 'Please select users first', severity: 'warning' });
      return;
    }

    if (window.confirm(`Are you sure you want to ${action} ${selectedUsers.length} user(s)?`)) {
      try {
        await userAPI.bulkAction({ action, user_ids: selectedUsers });
        setSnackbar({ open: true, message: `Users ${action}d successfully`, severity: 'success' });
        setSelectedUsers([]);
        fetchUsers();
      } catch (error) {
        const errorMsg = error.response?.data?.message || `Failed to ${action} users`;
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      }
    }
  };

  const handleResetPassword = async () => {
    if (!canUpdateUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to reset passwords', severity: 'error' });
      return;
    }

    setResetting(true);
    try {
      await userAPI.resetPassword(passwordUser.id, passwordData);
      setSnackbar({ open: true, message: 'Password reset successfully', severity: 'success' });
      setShowPasswordDialog(false);
      setPasswordUser(null);
      setPasswordData({ password: '', password_confirmation: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to reset password';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setResetting(false);
    }
  };

  const handleEdit = (user) => {
    if (!canUpdateUsers) {
      setSnackbar({ open: true, message: 'You do not have permission to update users', severity: 'error' });
      return;
    }

    setFormData({
      name: user.name,
      email: user.email,
      phone_number: user.phone_number || '',
      password: '',
      password_confirmation: '',
      role_id: user.role_id,
      staff_category: user.staff_category || '',
      is_active: user.is_active
    });
    setEditingUser(user);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      password: '',
      password_confirmation: '',
      role_id: '',
      staff_category: '',
      is_active: true
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleSelectAll = (e) => {
    if (!canUseBulkActions) return;
    setSelectedUsers(e.target.checked ? users.map(u => u.id) : []);
  };

  const handleSelectUser = (id) => {
    if (!canUseBulkActions) return;
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Card>
          <CardContent>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!canReadUsers) {
    return (
      <Alert severity="error">
        You do not have permission to view user management.
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage system users and their roles
            </Typography>
          </Grid>
          {canCreateUsers && (
            <Grid item>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowForm(true)}
              >
                Add User
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              {(searchInput || roleFilter || statusFilter !== '') && (
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                    setRoleFilter('');
                    setStatusFilter('');
                    setPage(1);
                  }}
                >
                  Clear
                </Button>
              )}
            </Grid>
          </Grid>

          {canUseBulkActions && selectedUsers.length > 0 && (
            <Box mt={2} display="flex" gap={1}>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {selectedUsers.length} selected
              </Typography>
              {canUpdateUsers && (
                <>
                  <Button size="small" onClick={() => handleBulkAction('activate')}>
                    Activate
                  </Button>
                  <Button size="small" onClick={() => handleBulkAction('deactivate')}>
                    Deactivate
                  </Button>
                </>
              )}
              {canDeleteUsers && (
                <Button size="small" color="error" onClick={() => handleBulkAction('delete')}>
                  Delete
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.length === users.length && users.length > 0}
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      onChange={handleSelectAll}
                      disabled={!canUseBulkActions}
                    />
                  </TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          disabled={!canUseBulkActions}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {user.staff_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <UserAvatar user={user} size={32} />
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.phone_number || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role_relation?.name || 'No Role'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {user.staff_category ? (
                          <Chip
                            label={user.staff_category === 'professional' ? 'Professional' : 'Support'}
                            size="small"
                            variant="outlined"
                            color={user.staff_category === 'professional' ? 'info' : 'secondary'}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.is_active ? 'success' : 'default'}
                          icon={user.is_active ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {canUpdateUsers && (
                            <Tooltip title="Edit User">
                              <IconButton size="small" color="primary" onClick={() => handleEdit(user)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canUpdateUsers && (
                            <Tooltip title="Reset Password">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => {
                                  setPasswordUser(user);
                                  setShowPasswordDialog(true);
                                }}
                              >
                                <LockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canUpdateUsers && (
                            <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                              <Switch
                                checked={user.is_active}
                                onChange={() => handleToggleStatus(user.id)}
                                size="small"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#d32f2f',
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#d32f2f',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {canDeleteUsers && (
                            <Tooltip title="Delete User">
                              <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(user)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPageSize(parseInt(size)); setPage(1); }}
          />
        </CardContent>
      </Card>

      <Dialog open={showForm} onClose={resetForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="Email (Optional)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  required
                  placeholder="e.g., 0700123456 or 256700123456"
                  helperText="Enter phone number (will be normalized to 256 format)"
                />
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role_id}
                    label="Role"
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Staff Category</InputLabel>
                  <Select
                    value={formData.staff_category}
                    label="Staff Category"
                    onChange={(e) => setFormData({ ...formData, staff_category: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="professional">Professional (Non-Teaching Staff)</MenuItem>
                    <MenuItem value="support">Support Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {editingUser && editingUser.staff_id && (
                <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={editingUser.staff_id}
                    disabled
                    helperText="Auto-generated, cannot be changed"
                  />
                </Grid>
              )}
              {!editingUser && (
                <>
                  <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.password_confirmation}
                      onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                      required={!editingUser}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.is_active}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || (editingUser ? !canUpdateUsers : !canCreateUsers)}
          >
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {editingUser ? 'Updating...' : 'Saving...'}
              </Box>
            ) : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user <strong>{userToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting || !canDeleteUsers}>
            {deleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Deleting...
              </Box>
            ) : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reset Password for {passwordUser?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showResetPassword ? "text" : "password"}
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowResetPassword(!showResetPassword)} edge="end">
                          {showResetPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showResetConfirmPassword ? "text" : "password"}
                  value={passwordData.password_confirmation}
                  onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)} edge="end">
                          {showResetConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)} disabled={resetting}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="primary" disabled={resetting || !canUpdateUsers}>
            {resetting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Resetting...
              </Box>
            ) : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCreatedUserDialog} onClose={() => setShowCreatedUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          User Created Successfully!
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Save these credentials! The password won't be shown again.
          </Alert>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {createdUserCredentials.loginType}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body1" fontWeight="medium">
                {createdUserCredentials.loginId}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(createdUserCredentials.loginId);
                  setSnackbar({ open: true, message: `${createdUserCredentials.loginType} copied!`, severity: 'success' });
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Temporary Password
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold" color="primary">
                {createdUserCredentials.password}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(createdUserCredentials.password);
                  setSnackbar({ open: true, message: 'Password copied!', severity: 'success' });
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
          <Alert severity="warning" sx={{ mt: 2 }}>
            User will be required to change this password on first login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreatedUserDialog(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
